import clsx from "clsx"
import React from "react"
import { observer } from "mobx-react-lite"

import { Icon } from "@components/ui/Icon/Icon"
import Text from "@components/ui/Typography/Text"
import { useStore } from "@store/index"
import { pluralize } from "@utils/textUtils"

import NavItemTooltip from "./NavItemTooltip"

import navItemStyles from "./NavItem/NavItem.module.sass"
import styles from "./Sidebar.module.sass"

export interface HistoryNavItemProps {
  /**
   * sidebar is expanded, the item is rendered as a labeled row
   */
  expanded: boolean
}

/**
 * Opens the query history ("Recent Activity") panel.
 * The panel itself is rendered by the fact finder, so this entry is only
 * shown on the screens where that panel is mounted.
 */
export const HistoryNavItem: React.FC<HistoryNavItemProps> = observer(
  ({ expanded }) => {
    const { solutionsStore, workbookReportsStore } = useStore()

    const totalComplete = workbookReportsStore.totalCompletedReports
    const totalStarted = workbookReportsStore.totalReportsToNotify

    if (!solutionsStore.isRecentActivityAvailable) return null

    const handleClick = () => {
      solutionsStore.showRecentActivity(true)
    }

    const badge =
      totalStarted > 0 ? (
        <div
          className={styles.navItemBadge}
          key={`${totalComplete}/${totalStarted}`}
        >
          {totalComplete === totalStarted
            ? totalStarted
            : `${totalComplete} / ${totalStarted}`}
        </div>
      ) : null

    const renderReportsStatus = () =>
      totalComplete < totalStarted ? (
        <>
          <Text variant="inherit" color="inherit">
            Report generation process started for {totalStarted} new{" "}
            {pluralize("request", totalStarted !== 1)}
          </Text>
          <br />
          <Text variant="inherit" color="inherit">
            {totalComplete > 0
              ? `${totalComplete} of reports ${
                  totalComplete === 1 ? "is" : "are"
                } completed and ready to view`
              : "Once process completed you'll be able to find you reports here"}
          </Text>
        </>
      ) : (
        <>
          {totalStarted > 1
            ? `All ${totalStarted} of new reports are completed`
            : "Your report is completed"}{" "}
          and ready to view
        </>
      )

    const tooltip = totalStarted > 0 ? renderReportsStatus() : "History"

    if (expanded)
      return (
        <div
          onClick={handleClick}
          role="button"
          tabIndex={0}
          className={clsx(navItemStyles.expandMenuItem, {
            [navItemStyles.active]: solutionsStore.isRecentActivityShown,
          })}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") handleClick()
          }}
        >
          <span>
            <Icon className={navItemStyles.optionIcon} name="retry" />
            <span>History</span>
          </span>
          {badge}
        </div>
      )

    return (
      <NavItemTooltip text={tooltip}>
        <div
          onClick={handleClick}
          role="button"
          tabIndex={0}
          className={clsx(navItemStyles.root, styles.navItem, {
            [navItemStyles.active]: solutionsStore.isRecentActivityShown,
          })}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") handleClick()
          }}
        >
          <span className={navItemStyles.icon}>
            <Icon name="retry" />
            <span className={navItemStyles.badge}>{badge}</span>
          </span>
        </div>
      </NavItemTooltip>
    )
  }
)

export default HistoryNavItem
